import axios from "axios";

const getApiBaseUrl = () => {
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const baseUrl = import.meta.env.VITE_API_URL || "";
    if (baseUrl && !baseUrl.includes("/api")) {
      return `${baseUrl}/api`;
    }
    return baseUrl || "/api";
  } else {
    return "/api";
  }
};

const API_CONFIG = {
  BASE_URL: getApiBaseUrl(),
  TIMEOUT: 30000,
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000,
};

class ApiService {
  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.requestInterceptors = [];
    this.responseInterceptors = [];

    this.setupInterceptors();
  }

  // Setup default interceptors
  setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        config.withCredentials = true;

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }

        if (config.method === "get") {
          config.params = { ...config.params, _t: Date.now() };
        }

        config.headers["X-Request-ID"] = this.generateRequestId();
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor - JUST pass through errors, no refresh logic
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Just pass the error - let admin.service.js handle auth errors
        return Promise.reject(error);
      },
    );
  }

  // Unique request ID
  generateRequestId() {
    return "req_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
  }

  async get(url, config = {}) {
    try {
      const finalConfig = {
        withCredentials: true,
        ...config,
      };

      const response = await this.instance.get(url, finalConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async post(url, data, config = {}) {
    try {
      // Check if data is FormData
      const isFormData = data instanceof FormData;

      const finalConfig = {
        withCredentials: true,
        headers: {
          // Only set Content-Type to multipart/form-data if it's FormData
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          ...config.headers,
        },
        ...config,
      };

      // If it's FormData, send as-is (don't stringify)
      const body = isFormData ? data : JSON.stringify(data);

      const response = await this.instance.post(url, body, finalConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async post(url, data, config = {}) {
    try {
      const isFormData = data instanceof FormData;

      const finalConfig = {
        withCredentials: true,
        ...config,
        headers: {
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          ...config.headers,
        },
      };

      if (isFormData) {
        delete finalConfig.headers["Content-Type"];
      }

      const body = isFormData ? data : JSON.stringify(data);
      const response = await this.instance.post(url, body, finalConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async put(url, data, config = {}) {
    try {
      const isFormData = data instanceof FormData;

      const finalConfig = {
        withCredentials: true,
        ...config,
        headers: {
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          ...config.headers,
        },
      };

      if (isFormData) {
        delete finalConfig.headers["Content-Type"];
      }

      const body = isFormData ? data : JSON.stringify(data);
      const response = await this.instance.put(url, body, finalConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async patch(url, data, config = {}) {
    try {
      const finalConfig = {
        withCredentials: true,
        ...config,
      };

      const response = await this.instance.patch(url, data, finalConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete(url, config = {}) {
    try {
      const finalConfig = {
        withCredentials: true,
        ...config,
      };

      const response = await this.instance.delete(url, finalConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // File upload method
  async upload(url, formData, onProgress) {
    try {
      const response = await this.instance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onProgress(progress);
          }
        },
      });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.get("/health");
      return {
        ...response,
        serverTime: new Date().toISOString(),
        clientTime: new Date().toISOString(),
      };
    } catch (error) {
      return {
        success: false,
        error: "Health check failed",
        message: "Unable to connect to server",
      };
    }
  }

  // Error handler
  handleError(error) {
    if (error.response?.data) {
      // Server responded with error
      return {
        success: false,
        status: error.response.status,
        error: error.response.data.error || "Unknown error",
        message: error.response.data.message || "An error occurred",
        timestamp: error.response.data.timestamp || new Date().toISOString(),
        data: error.response.data.data || null,
      };
    }

    if (error.request) {
      // Request made but no response
      return {
        success: false,
        status: 0,
        error: "Network Error",
        message:
          "Unable to connect to server. Please check your internet connection.",
        timestamp: new Date().toISOString(),
      };
    }

    // Something else happened
    return {
      success: false,
      status: -1,
      error: "Request Error",
      message: error.message || "An unexpected error occurred",
      timestamp: new Date().toISOString(),
    };
  }

  // Retry mechanism
  async retryRequest(request, retries = API_CONFIG.RETRY_COUNT) {
    for (let i = 0; i < retries; i++) {
      try {
        return await request();
      } catch (error) {
        if (i === retries - 1) throw error;
        await new Promise((resolve) =>
          setTimeout(resolve, API_CONFIG.RETRY_DELAY * (i + 1)),
        );
      }
    }
    throw new Error("Max retries reached");
  }

  isSuccess(response) {
    return response && response.success === true;
  }

  // handle API responses consistently
  handleResponse(response, successMessage = null) {
    if (this.isSuccess(response)) {
      return response;
    } else {
      throw new Error(response.message || "Request failed");
    }
  }
}

export const apiService = new ApiService();

// Export configure
export const configureApi = (config = {}) => {
  if (config.baseURL) {
    apiService.instance.defaults.baseURL = config.baseURL;
  }
  if (config.timeout) {
    apiService.instance.defaults.timeout = config.timeout;
  }
  if (config.headers) {
    apiService.instance.defaults.headers = {
      ...apiService.instance.defaults.headers,
      ...config.headers,
    };
  }
  return apiService;
};
